import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CircleDashed,
  CircleDivide,
  CircleDot,
  CircleDotDashed,
  CircleEllipsis,
  CircleEqual,
  CircleGauge,
  CircleMinus,
  CircleOff,
  CirclePlus,
  CircleSlash,
  CircleSmall,
  CircleStop,
  CircleX,
  Diameter,
  Radius
} from 'lucide-react';
import s from './Interactive.module.scss';
import cn from 'classnames';

const iconComponents = [
  CircleDashed,
  CircleDivide,
  CircleDot,
  CircleDotDashed,
  CircleEllipsis,
  CircleEqual,
  CircleGauge,
  CircleMinus,
  CircleOff,
  CirclePlus,
  CircleSlash,
  CircleSmall,
  CircleStop,
  CircleX,
  Diameter,
  Radius
];

const ICON_COUNT = 16;

interface IconPosition {
  id: string;
  x: number;
  y: number;
  icon: React.ComponentType<any>;
  opacity: number;
  dx: number;
  dy: number;
  duration: number;
}

export default function Interactive() {
  const [icons, setIcons] = useState<IconPosition[]>([]);
  const [clickedIcons, setClickedIcons] = useState<Set<string>>(new Set());
  const idCounter = useRef(0);
  const removedIds = useRef(new Set<string>());
  const timeouts = useRef<number[]>([]);
  const mounted = useRef(true);

  const generateRandomIcon = (): IconPosition => {
    const IconComponent = iconComponents[Math.floor(Math.random() * iconComponents.length)];
    const isLeft = Math.random() < 0.5;
    const x = isLeft ? Math.random() * 22 : 78 + Math.random() * 22;
    const y = 6 + Math.random() * 88;
    const angle = Math.random() * Math.PI * 2;
    const distance = 70 + Math.random() * 110;

    return {
      id: `${Date.now()}-${idCounter.current++}`,
      x,
      y,
      icon: IconComponent,
      opacity: 0.22 + Math.random() * 0.38,
      dx: Math.cos(angle) * distance,
      dy: Math.sin(angle) * distance,
      duration: 9000 + Math.random() * 7000
    };
  };

  const spawnRef = useRef<() => void>(() => {});
  spawnRef.current = () => {
    setIcons(prev => [...prev, generateRandomIcon()]);
  };

  const schedule = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      timeouts.current = timeouts.current.filter(item => item !== id);
      if (mounted.current) fn();
    }, ms);
    timeouts.current.push(id);
  }, []);

  const removeAndReplace = (iconId: string) => {
    if (removedIds.current.has(iconId)) return;
    removedIds.current.add(iconId);

    setIcons(prev => prev.filter(icon => icon.id !== iconId));
    setClickedIcons(prev => {
      if (!prev.has(iconId)) return prev;
      const next = new Set(prev);
      next.delete(iconId);
      return next;
    });

    schedule(() => spawnRef.current(), 500 + Math.random() * 1200);
  };

  const handleIconClick = (iconId: string) => {
    setClickedIcons(prev => new Set(prev).add(iconId));
  };

  useEffect(() => {
    mounted.current = true;

    for (let i = 0; i < ICON_COUNT; i++) {
      schedule(() => spawnRef.current(), i * 320);
    }

    return () => {
      mounted.current = false;
      timeouts.current.forEach(clearTimeout);
      timeouts.current = [];
    };
  }, [schedule]);

  return (
    <div className={s.root}>
      {icons.map(({ id, x, y, icon: IconComponent, opacity, dx, dy, duration }) => (
        <div
          key={id}
          className={cn(s.icon, s.alive, { [s.clicked]: clickedIcons.has(id) })}
          style={{
            left: `${x}%`,
            top: `${y}%`,
            '--target-opacity': opacity,
            '--dx': `${dx}px`,
            '--dy': `${dy}px`,
            '--duration': `${duration}ms`
          } as React.CSSProperties}
          onClick={() => handleIconClick(id)}
          onAnimationEnd={(event) => {
            if (event.target !== event.currentTarget) return;
            removeAndReplace(id);
          }}
        >
          <span
            className={s.glyph}
            onAnimationEnd={() => removeAndReplace(id)}
          >
            <IconComponent size={24} />
          </span>
        </div>
      ))}
    </div>
  );
}
